import { compareAsc } from "date-fns";
import { parseDateString, toOrgDateOnly } from "@/utils/dateUtils";

export interface AllocationForFirstLookup {
  id?: string;
  from_date?: string;
  to_date?: string;
}

/**
 * Reduce an allocation bound to a local-midnight Date on its org calendar day.
 * The API sends the bounds as RFC3339 UTC-midnight instants, so they go through
 * toOrgDateOnly first; date-only strings go through parseDateString.
 */
function toCalendarDay(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  return parseDateString(toOrgDateOnly(value));
}

/**
 * D1 (#1685): earliest live allocation whose to_date >= today; tie on from_date ->
 * smallest id.
 *
 * Allocations of a previous stay (already ended) are ignored. An entry with a
 * missing or unparseable bound is skipped. Soft-deleted rows need no check here:
 * the list endpoint already excludes them.
 *
 * @param today the org calendar day, "yyyy-MM-dd"
 */
export function findFirstAllocation<T extends AllocationForFirstLookup>(
  allocations: readonly T[] | undefined,
  today: string,
): T | undefined {
  const todayDay = parseDateString(today);
  if (!allocations || allocations.length === 0 || !todayDay) return undefined;

  let first: { allocation: T; from: Date } | undefined;
  for (const allocation of allocations) {
    const from = toCalendarDay(allocation.from_date);
    const to = toCalendarDay(allocation.to_date);
    if (!from || !to) continue;
    if (compareAsc(to, todayDay) < 0) continue;

    if (!first) {
      first = { allocation, from };
      continue;
    }
    const order = compareAsc(from, first.from);
    const earlierTie = order === 0 && (allocation.id ?? "") < (first.allocation.id ?? "");
    if (order < 0 || earlierTie) {
      first = { allocation, from };
    }
  }
  return first?.allocation;
}
